import React from 'react'
import { FormikProvider, useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import { Trainer } from '../../models/Trainer'
import { userCanCreateGlobalTrainer } from '../../state/calculations/permissions/userCan'
import useCreateTrainer from '../../state/mutations/useCreateTrainer'
import { useSession } from '../../state/session'
import setFormErrors from '../../utilities/setFormErrors'
import useFocusInputWithError from '../../utilities/useFocusInputWithError'
import { CheckboxFormik } from '../basics/Checkbox/Checkbox'
import FormButtons from '../basics/FormButtons/FormButtons'
import { TextInputFormik } from '../basics/TextInput/TextInput'

import styles from './EditTrainerForm.module.scss'

// TODO: Does only allow creating trainers for now, editing TBD

type EditTrainerFormProps = {
  onClose?(trainer?: Trainer): void
}

export default React.memo(function EditTrainerForm({ onClose }: EditTrainerFormProps) {
  const { t } = useTranslation()

  const { user: currentUser } = useSession()

  const createTrainer = useCreateTrainer()
  const formik = useFormik({
    initialValues: { firstname: '', lastname: '', global: false, username: '' },
    enableReinitialize: true,
    onSubmit: async (values, { setStatus, setErrors }) => {
      try {
        setStatus()

        values.username = `${values.firstname.toLowerCase()}.${values.lastname.toLowerCase()}`

        const trainer = await createTrainer.mutateAsync(values)

        onClose?.(trainer)
      } catch (err) {
        setFormErrors({ setStatus, setErrors, t }, err)
      }
    },
  })

  useFocusInputWithError(formik)

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={styles.EditTrainerForm}>
        <h1>{t('trainer.create new trainer')}</h1>

        <TextInputFormik name="firstname" label={t('trainer.field firstname')} required />
        <TextInputFormik name="lastname" label={t('trainer.field lastname')} required />

        {userCanCreateGlobalTrainer(currentUser) && <CheckboxFormik name="global" label={t('trainer.create global')} />}

        <FormButtons {...formik} onClose={() => onClose?.()} />
      </form>
    </FormikProvider>
  )
})
